import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Mail, Search, Send, User, Clock, X, Eye, Copy, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const EmailHistoryPage = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [copied, setCopied] = useState(false);

  useDocumentTitle(
    emails.length ? `${emails.length} e-mails enviados` : "E-mails",
    { ready: !loading },
  );

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/messages/emails`);
        setEmails(res.data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleCopy = async () => {
    if (!active?.body) return;
    try {
      await navigator.clipboard.writeText(active.body);
      setCopied(true);
      toast.success("Conteúdo copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return emails;
    return emails.filter(
      (e) =>
        (e.candidate_name || "").toLowerCase().includes(q) ||
        (e.to || "").toLowerCase().includes(q) ||
        (e.subject || "").toLowerCase().includes(q) ||
        (e.body || "").toLowerCase().includes(q)
    );
  }, [emails, search]);

  return (
    <div data-testid="email-history-page" className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
            Comunicações
          </div>
          <h1 className="heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950 flex items-center gap-3">
            <Mail className="w-8 h-8 text-slate-700" />
            Histórico de e-mails
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            Todos os e-mails enviados aos candidatos pela plataforma.
          </p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{filtered.length}</span>{" "}
            e-mail(s){" "}
            {search && (
              <span className="text-slate-500">de {emails.length} no total</span>
            )}
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="email-search-input"
              placeholder="Buscar por candidato, e-mail ou assunto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-slate-500">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="emails-empty-state"
            className="p-16 text-center"
          >
            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-5 h-5 text-slate-600" />
            </div>
            <h3 className="heading text-base font-semibold text-slate-900 mb-1">
              {emails.length === 0
                ? "Nenhum e-mail enviado ainda"
                : "Nenhum resultado para a busca"}
            </h3>
            <p className="text-sm text-slate-600 max-w-sm mx-auto">
              {emails.length === 0
                ? "Os e-mails que você enviar aos candidatos aparecerão aqui."
                : "Tente outra palavra-chave."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr>
                  <th className="border-b border-slate-200 py-3 px-6 text-xs font-medium text-slate-500 uppercase tracking-wider bg-slate-50/50">
                    Candidato
                  </th>
                  <th className="border-b border-slate-200 py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider bg-slate-50/50">
                    Para
                  </th>
                  <th className="border-b border-slate-200 py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider bg-slate-50/50">
                    Assunto
                  </th>
                  <th className="border-b border-slate-200 py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider bg-slate-50/50 w-44">
                    Enviado em
                  </th>
                  <th className="border-b border-slate-200 py-3 px-4 bg-slate-50/50 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={`email-row-${e.id}`}
                    onClick={() => setActive(e)}
                    className="hover:bg-slate-50 transition-colors cursor-pointer"
                  >
                    <td className="border-b border-slate-100 py-4 px-6 text-sm">
                      <div className="font-medium text-slate-900 inline-flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        {e.candidate_name || "Sem candidato vinculado"}
                      </div>
                    </td>
                    <td className="border-b border-slate-100 py-4 px-4 text-sm text-slate-600">
                      {e.to}
                    </td>
                    <td className="border-b border-slate-100 py-4 px-4 text-sm text-slate-700">
                      <div className="line-clamp-1 max-w-md">{e.subject}</div>
                    </td>
                    <td className="border-b border-slate-100 py-4 px-4 text-xs text-slate-500 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDateTime(e.sent_at)}
                      </span>
                    </td>
                    <td className="border-b border-slate-100 py-4 px-4 text-right">
                      <button
                        data-testid={`view-email-${e.id}`}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setActive(e);
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 hover:text-slate-950 bg-white border border-slate-200 hover:border-slate-400 rounded-md px-2.5 py-1.5 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {active && (
        <Dialog open onOpenChange={(o) => !o && setActive(null)}>
          <DialogContent
            data-testid="email-detail-modal"
            className="max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <DialogHeader className="border-b border-slate-100 pb-4">
              <DialogTitle className="heading text-xl font-bold text-slate-950 leading-tight pr-8">
                {active.subject || "(sem assunto)"}
              </DialogTitle>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-sm text-slate-600">
                <span className="inline-flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-slate-400" />
                  {active.candidate_name || "—"}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  {active.to}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs">
                  <Clock className="w-3 h-3 text-slate-400" />
                  {formatDateTime(active.sent_at)}
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                  <Send className="w-3 h-3" />
                  enviado
                </span>
              </div>
            </DialogHeader>

            <div className="pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500">
                  Conteúdo do e-mail
                </div>
                {active.body && (
                  <Button
                    data-testid="copy-email-button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopy}
                    className="h-7 text-xs"
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3 mr-1" />
                        Copiar
                      </>
                    )}
                  </Button>
                )}
              </div>

              {active.body ? (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-4">
                  <pre className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap font-sans break-words">
                    {active.body}
                  </pre>
                </div>
              ) : (
                <div
                  data-testid="email-body-empty"
                  className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-start gap-2.5"
                >
                  <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-amber-900 mb-0.5">
                      Conteúdo não disponível
                    </div>
                    <p className="text-xs text-amber-800 leading-relaxed">
                      Este e-mail foi enviado antes de a plataforma armazenar o
                      texto completo do corpo. A partir de agora, todos os e-mails
                      novos terão o conteúdo salvo e visualizável aqui.
                    </p>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setActive(null)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-700 p-1 rounded-md hover:bg-slate-100"
              data-testid="close-email-modal"
            >
              <X className="w-4 h-4" />
            </button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EmailHistoryPage;
